import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { authEvents } from '@/shared/lib/authEvents';
import { useToast } from '@/shared/ui-kit/ToastProvider/useToast';

interface AuthContextProps {
  isAuthModalVisible: boolean;
  setAuthModalVisible: (value: boolean) => void;

  isLoggedIn: boolean;
  setLoggedIn: (value: boolean) => void;

  openAuthModal: () => void;
  closeAuthModal: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthModalVisible, setAuthModalVisible] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const { showError } = useToast();

  useEffect(() => {
    const access = localStorage.getItem('accessToken');
    const refresh = localStorage.getItem('refreshToken');
    if (access && refresh) setLoggedIn(true);
  }, []);

  useEffect(() => {
    const unsubLogin = authEvents.subscribe('LOGIN_SUCCESS', () => {
      setLoggedIn(true);
      setAuthModalVisible(false);
    });

    const unsubExpired = authEvents.subscribe('SESSION_EXPIRED', () => {
      setLoggedIn(false);
      setAuthModalVisible(true);
      showError('Время сессии истекло');
    });

    return () => {
      unsubLogin();
      unsubExpired();
    };
  }, [showError]);

  const openAuthModal = () => setAuthModalVisible(true);
  const closeAuthModal = () => setAuthModalVisible(false);

  const value = useMemo(
    () => ({
      isAuthModalVisible,
      setAuthModalVisible,
      isLoggedIn,
      setLoggedIn,
      openAuthModal,
      closeAuthModal,
    }),
    [isAuthModalVisible, isLoggedIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
